import React, {
  useState,
  useMemo,
  useEffect,
} from "react"

import { ICONS } from "../Images"
import _ from "lodash"

import {
  Form,
  Button,
  ButtonToolbar,
  Schema,
  toaster,
  Message,
} from "rsuite"

import { useNavigate } from "react-router-dom"

import {
  useUserLoginMutation,
  useResetPasswordMutation,
} from "../Redux/actions/user.action"

import {
  Loader,
  ToastMessage,
} from "../components/index"

import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  Fade,
  InputLabel,
} from "@mui/material";

import {
  Close,
  EmailOutlined,
  PasswordOutlined,
  Visibility,
  VisibilityOff,
  ArrowForward,
  SchoolOutlined,
  SecurityOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";

const { StringType } = Schema.Types;

export default function LoginPage() {
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /* =========================
     STATE
  ========================= */

  const [status, setStatus] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [email, setEmail] = useState("");
  const [new_password, setNewpassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [resetPassword, setResetPassword] = useState(false);

  const [
    loginUserAction,
    loginUserSatate,
  ] = useUserLoginMutation();

  const [
    resetPasswordAction,
    resetPasswordState,
  ] = useResetPasswordMutation();

  /* =========================
     LOGIN STATUS
  ========================= */

  const updateConfirmationStatus = useMemo(() => {
    if (loginUserSatate.isSuccess) {
      localStorage.setItem(
        "current_user",
        JSON.stringify(loginUserSatate.data)
      );

      return {
        show: true,
        message: loginUserSatate.data.message,
        type: "success",
        redirect: "/",
      };
    }

    if (loginUserSatate.isError) {
      return {
        show: true,
        message:
          loginUserSatate.error?.data?.detail ||
          "Unable to login. Please try again.",
        type: "error",
        redirect: null,
      };
    }

    return null;
  }, [loginUserSatate]);

  useEffect(() => {
    if (updateConfirmationStatus) {
      setStatus(updateConfirmationStatus);
    }
  }, [updateConfirmationStatus]);

  /* =========================
     RESET PASSWORD STATUS
  ========================= */

  const resetPasswordStatus = useMemo(() => {
    if (resetPasswordState.isSuccess) {
      const message = _.get(
        resetPasswordState,
        "data.message",
        "Password reset successfully."
      );

      toaster.push(
        <Message type="success">
          {message}
        </Message>,
        {
          placement: "topCenter",
        }
      );

      return {
        show: true,
        type: "success",
        message,
      };
    }

    if (resetPasswordState.isError) {
      return {
        show: true,
        type: "error",
        message:
          "Error while resetting the password!",
      };
    }

    return null;
  }, [resetPasswordState]);

  useEffect(() => {
    if (resetPasswordStatus) {
      setStatus(resetPasswordStatus);

      if (resetPasswordStatus.type === "success") {
        setEmail("");
        setNewpassword("");
        setResetPassword(false);
      }
    }
  }, [resetPasswordStatus]);

  /* =========================
     VALIDATION
  ========================= */

  const model = Schema.Model({
    email: StringType()
      .isEmail("Please enter a valid email.")
      .isRequired("Email is required."),

    password: StringType().isRequired(
      "Password is required."
    ),
  });

  /* =========================
     LOGIN
  ========================= */

  const handleSubmit = (formData) => {
    loginUserAction(formData);
  };

  /* =========================
     RESET PASSWORD
  ========================= */

  const handleResetPass = () => {
    if (!email || !new_password) {
      toaster.push(
        <Message type="info">
          Please fill the email and new password.
        </Message>,
        {
          placement: "topCenter",
        }
      );

      return;
    }

    resetPasswordAction({
      email,
      new_password,
    });
  };

  /* =========================
     LOGOUT
  ========================= */

  const handleLogout = () => {
    localStorage.clear();

    toaster.push(
      <Message type="success">
        User logout successful
      </Message>,
      {
        placement: "topCenter",
      }
    );

    navigate("/");
  };

  return (
    <Box className="login-page">

      {/* ==========================================
                BACKGROUND DECORATION
            ========================================== */}

      <Box className="background-decoration">
        <Box className="blur-circle circle-one" />
        <Box className="blur-circle circle-two" />
        <Box className="blur-circle circle-three" />
      </Box>

      {/* ==========================================
                MAIN CONTAINER
            ========================================== */}

      <Box className="login-container">

        {/* ======================================
                    LEFT BRANDING SECTION
                ====================================== */}

        <Box className="branding-section">

          <Box className="branding-image">
            <img
              src={ICONS.login_banner}
              alt="Education platform"
            />
          </Box>

          <Box className="branding-overlay" />

          <Box className="branding-content">

            {/* Logo */}

            <Box
              className="brand-logo"
              onClick={() => navigate("/")}
            >
              <Box className="logo-icon">
                <SchoolOutlined />
              </Box>

              <Typography className="brand-name">
                EduCare
              </Typography>
            </Box>

            {/* Main Text */}

            <Box className="branding-text">

              <Typography className="branding-title">
                Welcome back to
                <br />
                your learning
                <br />
                journey.
              </Typography>

              <Typography className="branding-description">
                Sign in to access your institution
                dashboard, manage your academic
                activities and stay connected.
              </Typography>

            </Box>

            {/* Features */}

            <Box className="feature-list">

              <Box className="feature-item">
                <CheckCircleOutline />
                <Typography>
                  Manage your institution
                </Typography>
              </Box>

              <Box className="feature-item">
                <CheckCircleOutline />
                <Typography>
                  Track academic activities
                </Typography>
              </Box>

              <Box className="feature-item">
                <CheckCircleOutline />
                <Typography>
                  Secure & reliable platform
                </Typography>
              </Box>

            </Box>

          </Box>

        </Box>

        {/* ======================================
                    LOGIN SECTION
                ====================================== */}

        <Box className="form-section">

          <Box className="form-container">

            {/* Mobile Logo */}

            {isMobile && (
              <Box
                className="mobile-brand"
                onClick={() => navigate("/")}
              >
                <Box className="mobile-logo-icon">
                  <SchoolOutlined />
                </Box>

                <Typography>
                  EduCare
                </Typography>
              </Box>
            )}

            {/* Header */}

            <Box className="form-header">

              <Typography className="form-title">
                Sign in
              </Typography>

              <Typography className="form-subtitle">
                Enter your credentials to continue
              </Typography>

            </Box>

            {/* Login Form */}

            <Form
              fluid
              model={model}
              onSubmit={handleSubmit}
            >

              {/* EMAIL */}

              <Form.Group className="form-group">

                <Form.ControlLabel>
                  Email address
                </Form.ControlLabel>

                <Form.Control
                  name="email"
                  type="email"
                  placeholder="name@institution.com"
                  size="lg"
                />

              </Form.Group>

              {/* PASSWORD */}

              <Form.Group className="form-group">
                <Box className="password-label">
                  <Form.ControlLabel>
                    Password
                  </Form.ControlLabel>

                  <Typography
                    className="forgot-link"
                    onClick={() => setResetPassword(true)}
                  >
                    Forgot password?
                  </Typography>
                </Box>

                <Box className="password-input-wrapper">
                  <Form.Control
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    size="lg"
                  />

                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="password-toggle"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Form.Group>

              {/* LOGIN BUTTON */}

              <Form.Group className="submit-group">

                <ButtonToolbar>

                  <Button
                    appearance="primary"
                    block
                    type="submit"
                    className="login-button"
                    loading={
                      loginUserSatate.isLoading
                    }
                  >
                    <span>
                      Sign in
                    </span>

                    <ArrowForward
                      fontSize="small"
                    />
                  </Button>

                </ButtonToolbar>

              </Form.Group>

              {/* DIVIDER */}

              <Box className="divider-container">

                <Divider />

                <Typography>
                  OR
                </Typography>

                <Divider />

              </Box>

              {/* REGISTER */}

              <Box className="register-section">

                <Typography>
                  Don't have an account?
                </Typography>

                <Typography
                  component="span"
                  className="register-link"
                  onClick={() =>
                    navigate(
                      "/auth/signup"
                    )
                  }
                >
                  Create an account
                </Typography>

              </Box>

              {/* LOGOUT */}

              <Button
                appearance="subtle"
                block
                onClick={handleLogout}
                className="logout-button"
              >
                Logout
              </Button>

            </Form>

            {/* SECURITY */}

            <Box className="security-note">

              <SecurityOutlined />

              <Typography>
                Your information is securely
                protected.
              </Typography>

            </Box>

            <Typography className="copyright">
              © {new Date().getFullYear()} EduCare.
              All rights reserved.
            </Typography>

          </Box>

        </Box>

      </Box>

      {/* ==========================================
                RESET PASSWORD DIALOG
            ========================================== */}

      <Dialog
        open={resetPassword}
        onClose={() => setResetPassword(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Fade}
        PaperProps={{
          className: "reset-dialog",
        }}
      >

        <DialogContent sx={{ p: 0 }}>

          <Box className="reset-container">

            {/* HEADER */}

            <Box className="reset-header">

              <Box>
                <Typography className="reset-title">
                  Reset password
                </Typography>

                <Typography className="reset-subtitle">
                  Create a new password for your
                  account
                </Typography>
              </Box>

              <Tooltip title="Close">

                <IconButton
                  onClick={() =>
                    setResetPassword(false)
                  }
                  className="close-button"
                >
                  <Close />
                </IconButton>

              </Tooltip>

            </Box>

            <Divider />

            {/* FORM */}

            <Box className="reset-form">

              {/* EMAIL */}

              <Box>

                <InputLabel className="mui-label">
                  Email address
                </InputLabel>

                <TextField
                  fullWidth
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) =>
                    setEmail(
                      e.target.value
                    )
                  }
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined />
                      </InputAdornment>
                    ),
                  }}
                />

              </Box>

              {/* PASSWORD */}

              <Box>

                <InputLabel className="mui-label">
                  New password
                </InputLabel>

                <TextField
                  fullWidth
                  placeholder="Enter new password"
                  value={new_password}
                  onChange={(e) =>
                    setNewpassword(
                      e.target.value
                    )
                  }
                  type={
                    showNewPassword
                      ? "text"
                      : "password"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PasswordOutlined />
                      </InputAdornment>
                    ),

                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowNewPassword(
                              !showNewPassword
                            )
                          }
                        >
                          {showNewPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              </Box>

            </Box>

            {/* ACTIONS */}

            <Box className="reset-actions">

              <Button
                appearance="subtle"
                onClick={() => {
                  setEmail("");
                  setNewpassword("");
                  setResetPassword(false);
                }}
                className="cancel-button"
              >
                Cancel
              </Button>

              <Button
                appearance="primary"
                onClick={handleResetPass}
                loading={
                  resetPasswordState.isLoading
                }
                className="reset-button"
              >
                Reset password
              </Button>

            </Box>

          </Box>

        </DialogContent>

      </Dialog>

      {/* ==========================================
                LOADER
            ========================================== */}

      {(loginUserSatate.isLoading ||
        resetPasswordState.isLoading) && (
          <Loader show={true} />
        )}

      {/* ==========================================
                TOAST
            ========================================== */}

      {status.show && (
        <ToastMessage
          {...status}
          onClose={() => {
            setStatus({
              show: false,
              message: "",
              type: "",
            });
          }}
        />
      )}

      {/* ==========================================
                STYLES
            ========================================== */}

      <style>{`

                * {
                    box-sizing: border-box;
                }

                /* =====================================
                   PAGE
                ===================================== */

                .login-page {
                    min-height: 100vh;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                    position: relative;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            135deg,
                            #f8fafc 0%,
                            #eef2ff 50%,
                            #f8fafc 100%
                        );
                }

                /* =====================================
                   BACKGROUND
                ===================================== */

                .background-decoration {
                    position: fixed;
                    inset: 0;
                    pointer-events: none;
                    overflow: hidden;
                }

                .blur-circle {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px);
                    opacity: 0.35;
                }

                .circle-one {
                    width: 300px;
                    height: 300px;
                    background: #c7d2fe;
                    top: -100px;
                    left: -100px;
                }

                .circle-two {
                    width: 350px;
                    height: 350px;
                    background: #bfdbfe;
                    right: -120px;
                    bottom: -100px;
                }

                .circle-three {
                    width: 200px;
                    height: 200px;
                    background: #ddd6fe;
                    right: 35%;
                    top: 10%;
                }

                /* =====================================
                   MAIN CARD
                ===================================== */

                .login-container {
                    width: 100%;
                    max-width: 1180px;
                    min-height: 700px;
                    display: grid;
                    grid-template-columns: 1.05fr 0.95fr;
                    background: #ffffff;
                    border-radius: 28px;
                    overflow: hidden;
                    position: relative;
                    z-index: 2;
                    box-shadow:
                        0 25px 60px rgba(15, 23, 42, 0.12),
                        0 5px 15px rgba(15, 23, 42, 0.05);
                }

                /* =====================================
                   BRANDING
                ===================================== */

                .branding-section {
                    position: relative;
                    min-height: 700px;
                    overflow: hidden;
                    background: #312e81;
                    color: white;
                }

                .branding-image {
                    position: absolute;
                    inset: 0;
                }

                .branding-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    opacity: 0.45;
                }

                .branding-overlay {
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(
                            145deg,
                            rgba(49, 46, 129, 0.98),
                            rgba(30, 27, 75, 0.82),
                            rgba(15, 23, 42, 0.95)
                        );
                }

                .branding-content {
                    position: relative;
                    z-index: 2;
                    height: 100%;
                    min-height: 700px;
                    padding: 48px;
                    display: flex;
                    flex-direction: column;
                }

                /* =====================================
                   LOGO
                ===================================== */

                .brand-logo {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    cursor: pointer;
                    width: fit-content;
                }

                .logo-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,0.15);
                    border: 1px solid rgba(255,255,255,0.2);
                    backdrop-filter: blur(10px);
                }

                .brand-name {
                    font-size: 22px !important;
                    font-weight: 800 !important;
                    letter-spacing: -0.5px;
                }

                /* =====================================
                   BRAND TEXT
                ===================================== */

                .branding-text {
                    margin-top: auto;
                    margin-bottom: 40px;
                    max-width: 500px;
                }

                .branding-title {
                    font-size: clamp(
                        2.4rem,
                        4vw,
                        4rem
                    ) !important;
                    font-weight: 800 !important;
                    line-height: 1.08 !important;
                    letter-spacing: -2px !important;
                    margin-bottom: 22px !important;
                }

                .branding-description {
                    font-size: 16px !important;
                    line-height: 1.7 !important;
                    color: rgba(255,255,255,0.78);
                    max-width: 430px;
                }

                /* =====================================
                   FEATURES
                ===================================== */

                .feature-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }

                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 9px 13px;
                    border-radius: 10px;
                    background: rgba(255,255,255,0.08);
                    border: 1px solid rgba(255,255,255,0.1);
                    backdrop-filter: blur(10px);
                }

                .feature-item svg {
                    font-size: 17px;
                }

                .feature-item p {
                    font-size: 12px;
                    font-weight: 500;
                }

                /* =====================================
                   FORM SECTION
                ===================================== */

                .form-section {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #ffffff;
                    padding: 50px;
                }

                .form-container {
                    width: 100%;
                    max-width: 410px;
                }

                /* =====================================
                   FORM HEADER
                ===================================== */

                .form-header {
                    margin-bottom: 34px;
                }

                .form-title {
                    font-size: 36px !important;
                    font-weight: 800 !important;
                    color: #0f172a;
                    letter-spacing: -1.5px;
                    margin-bottom: 8px !important;
                }

                .form-subtitle {
                    color: #64748b;
                    font-size: 14px !important;
                }

                /* =====================================
                   RSUITE FORM
                ===================================== */

                .form-group {
                    margin-bottom: 23px !important;
                }

                .rs-form-group .rs-form-control-label {
                    font-size: 13px !important;
                    font-weight: 650 !important;
                    color: #334155 !important;
                    margin-bottom: 8px !important;
                }

                .rs-form-control {
                    border-radius: 12px !important;
                }

                .rs-form-control,
                .rs-form-control input {
                    background: #f8fafc !important;
                    border: 1px solid #e2e8f0 !important;
                    color: #0f172a !important;
                    border-radius: 12px !important;
                    font-size: 14px !important;
                    transition: all .2s ease !important;
                }

                .rs-form-control:hover,
                .rs-form-control input:hover {
                    border-color: #cbd5e1 !important;
                }

                .rs-form-control:focus-within {
                    border-color: #6366f1 !important;
                    background: #ffffff !important;
                    box-shadow:
                        0 0 0 4px
                        rgba(99,102,241,.10) !important;
                }

                /* =====================================
                   PASSWORD
                ===================================== */

                .password-label {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .forgot-link {
                    font-size: 12px !important;
                    color: #6366f1;
                    font-weight: 600 !important;
                    cursor: pointer;
                }

                .forgot-link:hover {
                    text-decoration: underline;
                }

                .password-input-wrapper {
                    width: 100%;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    position: relative;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    transition: all .2s ease;
                }

                .password-input-wrapper:focus-within {
                    border-color: #6366f1;
                    background: #ffffff;
                    box-shadow:
                        0 0 0 4px
                        rgba(99,102,241,.10);
                }

                .custom-password-input {
                    width: 100%;
                    height: 100%;
                    padding: 0 50px 0 14px;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 14px;
                    color: #0f172a;
                }

                .password-toggle {
                    position: absolute !important;
                    right: 5px;
                    color: #64748b !important;
                }

                /* =====================================
                   LOGIN BUTTON
                ===================================== */

                .submit-group {
                    margin-top: 28px !important;
                }

                .login-button {
                    height: 50px !important;
                    border-radius: 12px !important;
                    background:
                        linear-gradient(
                            135deg,
                            #6366f1,
                            #4f46e5
                        ) !important;
                    font-size: 14px !important;
                    font-weight: 700 !important;
                    display: flex !important;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    border: none !important;
                    box-shadow:
                        0 8px 20px
                        rgba(79,70,229,.25) !important;
                    transition: all .2s ease !important;
                }

                .login-button:hover {
                    transform: translateY(-1px);
                    box-shadow:
                        0 12px 25px
                        rgba(79,70,229,.3) !important;
                }

                /* =====================================
                   DIVIDER
                ===================================== */

                .divider-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin: 28px 0 20px;
                }

                .divider-container .MuiDivider-root {
                    flex: 1;
                }

                .divider-container p {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                }

                /* =====================================
                   REGISTER
                ===================================== */

                .register-section {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    flex-wrap: wrap;
                    color: #64748b;
                    font-size: 13px;
                }

                .register-link {
                    color: #4f46e5;
                    font-weight: 700 !important;
                    cursor: pointer;
                }

                .register-link:hover {
                    text-decoration: underline;
                }

                /* =====================================
                   LOGOUT
                ===================================== */

                .logout-button {
                    margin-top: 20px !important;
                    height: 42px !important;
                    border-radius: 10px !important;
                    border: 1px solid #e2e8f0 !important;
                    color: #64748b !important;
                    font-size: 13px !important;
                    background: transparent !important;
                }

                .logout-button:hover {
                    background: #f8fafc !important;
                    color: #334155 !important;
                }

                /* =====================================
                   SECURITY
                ===================================== */

                .security-note {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    margin-top: 28px;
                    color: #94a3b8;
                }

                .security-note svg {
                    font-size: 16px;
                }

                .security-note p {
                    font-size: 11px;
                }

                .copyright {
                    text-align: center;
                    color: #cbd5e1;
                    font-size: 10px !important;
                    margin-top: 15px !important;
                }

                /* =====================================
                   MOBILE BRAND
                ===================================== */

                .mobile-brand {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    margin-bottom: 40px;
                    cursor: pointer;
                }

                .mobile-brand p {
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .mobile-logo-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #eef2ff;
                    color: #4f46e5;
                }

                /* =====================================
                   RESET DIALOG
                ===================================== */

                .reset-dialog {
                    border-radius: 22px !important;
                    overflow: hidden;
                    box-shadow:
                        0 25px 60px
                        rgba(15,23,42,.18) !important;
                }

                .reset-container {
                    padding: 28px;
                }

                .reset-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 20px;
                    padding-bottom: 22px;
                }

                .reset-title {
                    font-size: 24px !important;
                    font-weight: 800 !important;
                    color: #0f172a;
                    letter-spacing: -.5px;
                }

                .reset-subtitle {
                    font-size: 13px !important;
                    color: #64748b;
                    margin-top: 5px !important;
                }

                .close-button {
                    background: #f8fafc !important;
                }

                .reset-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    padding: 25px 0;
                }

                .mui-label {
                    color: #334155 !important;
                    font-size: 13px !important;
                    font-weight: 650 !important;
                    margin-bottom: 8px !important;
                }

                .reset-form .MuiTextField-root {
                    background: #f8fafc;
                }

                .reset-form .MuiOutlinedInput-root {
                    border-radius: 12px;
                }

                .reset-form
                .MuiOutlinedInput-root:hover
                .MuiOutlinedInput-notchedOutline {
                    border-color: #cbd5e1;
                }

                .reset-form
                .MuiOutlinedInput-root.Mui-focused
                .MuiOutlinedInput-notchedOutline {
                    border-color: #6366f1;
                }

                .reset-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding-top: 4px;
                }

                .cancel-button {
                    height: 44px !important;
                    border-radius: 10px !important;
                    border: 1px solid #e2e8f0 !important;
                    color: #64748b !important;
                }

                .reset-button {
                    height: 44px !important;
                    border-radius: 10px !important;
                    padding: 0 20px !important;
                    background: #4f46e5 !important;
                    font-weight: 700 !important;
                }

                /* =====================================
                   TABLET
                ===================================== */

                @media (max-width: 992px) {

                    .login-page {
                        padding: 16px;
                    }

                    .login-container {
                        grid-template-columns: 1fr;
                        max-width: 620px;
                        min-height: auto;
                    }

                    .branding-section {
                        min-height: 310px;
                    }

                    .branding-content {
                        min-height: 310px;
                        padding: 30px;
                    }

                    .branding-text {
                        margin-top: 70px;
                        margin-bottom: 0;
                    }

                    .branding-title {
                        font-size: 2.4rem !important;
                    }

                    .feature-list {
                        display: none;
                    }

                    .form-section {
                        padding: 45px 35px;
                    }
                }
                .password-input-wrapper {
    position: relative;
    width: 100%;
}

.password-input-wrapper .rs-form-control {
    width: 100%;
    padding-right: 50px !important;
}

.password-toggle {
    position: absolute !important;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    color: #64748b !important;
}

.password-toggle:hover {
    background: rgba(99, 102, 241, 0.08) !important;
}

                /* =====================================
                   MOBILE
                ===================================== */

                @media (max-width: 600px) {

                    .login-page {
                        min-height: 100dvh;
                        padding: 0;
                        align-items: stretch;
                    }

                    .login-container {
                        width: 100%;
                        min-height: 100dvh;
                        border-radius: 0;
                        display: block;
                        box-shadow: none;
                    }

                    .branding-section {
                        display: none;
                    }

                    .form-section {
                        min-height: 100dvh;
                        padding: 30px 22px;
                        align-items: flex-start;
                    }

                    .form-container {
                        max-width: 100%;
                        padding-top: 20px;
                    }

                    .form-title {
                        font-size: 31px !important;
                    }

                    .form-header {
                        margin-bottom: 28px;
                    }

                    .mobile-brand {
                        margin-bottom: 45px;
                    }

                    .reset-container {
                        padding: 22px;
                    }

                    .reset-title {
                        font-size: 21px !important;
                    }

                    .reset-actions {
                        flex-direction: column-reverse;
                    }

                    .reset-actions button {
                        width: 100%;
                    }
                }

                /* =====================================
                   SMALL MOBILE
                ===================================== */

                @media (max-width: 380px) {

                    .form-section {
                        padding: 25px 18px;
                    }

                    .form-title {
                        font-size: 28px !important;
                    }

                    .mobile-brand {
                        margin-bottom: 35px;
                    }
                }

            `}</style>

    </Box>
  )
}